import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Hiring v3 / Ticket D1 — Interview question pool admin.
// 92 seeded questions live in `interview_questions` as status='draft'
// (Ticket D4 seed loader). Owner curates them: edits text, promotes
// draft→active, or retires no-longer-useful ones. Net-new questions
// land as draft.
export function HiringSettingsQuestions({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [questions, setQuestions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [axisFilter, setAxisFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | <id>
  const [draft, setDraft] = useState(null);
  const [savingMsg, setSavingMsg] = useState('');

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (axisFilter) params.set('axis', axisFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await api('GET', `/api/admin/interview-questions${qs}`, null, headers);
      setQuestions(Array.isArray(r?.questions) ? r.questions : []);
    } catch (e) { setQuestions([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [statusFilter, axisFilter]);

  // Group by primary_axis for the list view.
  const grouped = (() => {
    const g = {};
    for (const q of questions) {
      const ax = q.primary_axis || '(none)';
      if (!g[ax]) g[ax] = [];
      g[ax].push(q);
    }
    return g;
  })();

  function openNew() {
    setEditing('new');
    setDraft({
      external_id: '', text: '', primary_axis: 'awareness',
      secondary_axes: [], roles: [], stages: [],
      strong_signals: '', weak_signals: '', interviewer_notes: '',
      // New questions land as active so they're immediately pickable in the
      // applicant interview-script picker (which filters to status='active').
      // The owner can still retire them later if they don't pull weight.
      status: 'active',
    });
  }
  function openEdit(q) {
    setEditing(q.id);
    setDraft({
      external_id: q.external_id || '',
      text: q.text || '',
      primary_axis: q.primary_axis || 'awareness',
      secondary_axes: Array.isArray(q.secondary_axes) ? q.secondary_axes : [],
      roles: Array.isArray(q.roles) ? q.roles : [],
      stages: Array.isArray(q.stages) ? q.stages : [],
      strong_signals: q.strong_signals || '',
      weak_signals: q.weak_signals || '',
      interviewer_notes: q.interviewer_notes || '',
      status: q.status || 'draft',
    });
  }
  function closeEditor() { setEditing(null); setDraft(null); }

  async function saveDraft() {
    if (!draft) return;
    setSavingMsg('Saving…');
    try {
      const payload = { ...draft };
      if (editing === 'new') {
        await api('POST', '/api/admin/interview-questions', payload, headers);
      } else {
        await api('PUT', `/api/admin/interview-questions/${editing}`, payload, headers);
      }
      setSavingMsg('Saved.');
      setTimeout(() => setSavingMsg(''), 1500);
      closeEditor();
      await load();
    } catch (e) { setSavingMsg(`Failed: ${e.message || e}`); }
  }
  async function setStatus(id, status) {
    try {
      await api('PUT', `/api/admin/interview-questions/${id}`, { status }, headers);
      await load();
    } catch (e) { alert(e.message || String(e)); }
  }
  async function deleteQuestion(q) {
    const ok = window.confirm(
      `Delete this question?\n\n"${(q.text || '').slice(0, 120)}${q.text && q.text.length > 120 ? '…' : ''}"\n\n` +
      `If it's been used in past interviews, it will be retired instead of fully deleted (so the response history stays valid).`
    );
    if (!ok) return;
    try {
      await api('DELETE', `/api/admin/interview-questions/${q.id}`, null, headers);
      await load();
    } catch (e) { alert(e.message || String(e)); }
  }

  // Axes available for selection — RUBRIC + the meta-categories used by the seed.
  const AXIS_OPTIONS = [
    'awareness', 'hospitality', 'collaboration', 'learning_posture',
    'calm_under_pressure', 'communication_quality', 'logistics_realism',
    'background', 'role_specific', 'curveball',
  ];
  const ROLE_OPTIONS = ['all', 'Barista', 'Shift Lead', 'Coffee Roaster', 'Kitchen', 'Asst Manager', 'Manager'];
  const STAGE_OPTIONS = ['phone_screen', 'in_person'];

  function toggleArr(arr, val) {
    return arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div>
          <div className="text-base font-semibold mb-1">Interview question pool</div>
          <div className="text-sm text-gray-500">
            Curate the questions managers can pick from when prepping interviews. New questions land as Draft until you promote.
          </div>
        </div>
        <button onClick={openNew}
          style={{ padding: '8px 14px', background: 'var(--gold)', color: '#fff',
                    border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + New question
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="retired">Retired</option>
        </select>
        <select value={axisFilter} onChange={e => setAxisFilter(e.target.value)}
          style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
          <option value="">All axes</option>
          {AXIS_OPTIONS.map(ax => <option key={ax} value={ax}>{ax.replace(/_/g, ' ')}</option>)}
        </select>
        <span style={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>
          {questions.length} question{questions.length === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(grouped).map(([axis, qs]) => (
            <div key={axis}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#757575',
                            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                {axis.replace(/_/g, ' ')} · {qs.length}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {qs.map(q => (
                  <div key={q.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 10, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        {q.external_id && <span style={{ color: '#888', fontSize: 11, marginRight: 6 }}>{q.external_id}</span>}
                        {q.text}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                        background: q.status === 'active' ? '#e6f4ea' : q.status === 'retired' ? '#fafafa' : '#fff8e6',
                                        color:      q.status === 'active' ? '#1e7d3a' : q.status === 'retired' ? '#888' : '#7a5b1a',
                                        border:     `1px solid ${q.status === 'active' ? '#c8e2c8' : q.status === 'retired' ? '#e0e0e0' : '#f0d9a3'}` }}>
                          {q.status}
                        </span>
                        <button onClick={() => openEdit(q)}
                          style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #d0d4d9',
                                    borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                          Edit
                        </button>
                        {q.status === 'draft' && (
                          <button onClick={() => setStatus(q.id, 'active')}
                            style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #c8e2c8',
                                      borderRadius: 4, background: '#f0f7f0', color: '#1e7d3a', cursor: 'pointer' }}>
                            Activate
                          </button>
                        )}
                        {q.status === 'active' && (
                          <button onClick={() => setStatus(q.id, 'retired')}
                            style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #d0d4d9',
                                      borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                            Retire
                          </button>
                        )}
                        <button onClick={() => deleteQuestion(q)}
                          style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #fecaca',
                                    borderRadius: 4, background: '#fef2f2', color: '#a13030', cursor: 'pointer' }}>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && draft && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,31,29,0.45)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
             onClick={closeEditor}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 12, padding: 18, maxWidth: 640, width: '100%',
                         maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
              {editing === 'new' ? 'New question' : `Edit question`}
            </div>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Text</label>
            <textarea value={draft.text}
              onChange={e => setDraft({ ...draft, text: e.target.value })}
              rows={3}
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                        fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
                External ID
                <input type="text" value={draft.external_id}
                  onChange={e => setDraft({ ...draft, external_id: e.target.value })}
                  style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }} />
              </label>
              <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
                Primary axis
                <select value={draft.primary_axis}
                  onChange={e => setDraft({ ...draft, primary_axis: e.target.value })}
                  style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }}>
                  {AXIS_OPTIONS.map(ax => <option key={ax} value={ax}>{ax.replace(/_/g, ' ')}</option>)}
                </select>
              </label>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Roles (which positions this applies to)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ROLE_OPTIONS.map(r => (
                  <label key={r} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #d0d4d9',
                                            borderRadius: 99, cursor: 'pointer',
                                            background: draft.roles.includes(r) ? 'var(--gold)' : '#fff',
                                            color: draft.roles.includes(r) ? '#fff' : '#333' }}>
                    <input type="checkbox" checked={draft.roles.includes(r)} style={{ display: 'none' }}
                      onChange={() => setDraft({ ...draft, roles: toggleArr(draft.roles, r) })} />
                    {r}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Stages</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {STAGE_OPTIONS.map(s => (
                  <label key={s} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #d0d4d9',
                                            borderRadius: 99, cursor: 'pointer',
                                            background: draft.stages.includes(s) ? 'var(--gold)' : '#fff',
                                            color: draft.stages.includes(s) ? '#fff' : '#333' }}>
                    <input type="checkbox" checked={draft.stages.includes(s)} style={{ display: 'none' }}
                      onChange={() => setDraft({ ...draft, stages: toggleArr(draft.stages, s) })} />
                    {s.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </div>

            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Strong-signal cues</label>
            <textarea value={draft.strong_signals}
              onChange={e => setDraft({ ...draft, strong_signals: e.target.value })}
              rows={2} placeholder="What a great answer sounds like."
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                        fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Weak-signal cues</label>
            <textarea value={draft.weak_signals}
              onChange={e => setDraft({ ...draft, weak_signals: e.target.value })}
              rows={2} placeholder="Red flags or shallow-answer patterns."
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                        fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Interviewer notes</label>
            <textarea value={draft.interviewer_notes}
              onChange={e => setDraft({ ...draft, interviewer_notes: e.target.value })}
              rows={2} placeholder="Optional follow-up framing for the manager."
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                        fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />

            <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
              Status
              <select value={draft.status}
                onChange={e => setDraft({ ...draft, status: e.target.value })}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="retired">Retired</option>
              </select>
            </label>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              {savingMsg && <span style={{ fontSize: 12, color: savingMsg.startsWith('Failed') ? '#b00020' : '#666', alignSelf: 'center' }}>{savingMsg}</span>}
              <button onClick={closeEditor}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 14px',
                          fontSize: 13, background: '#fff', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={saveDraft} disabled={!draft.text.trim() || !draft.primary_axis.trim()}
                style={{ border: 0, borderRadius: 6, padding: '6px 16px', fontSize: 13,
                          background: 'var(--gold)', color: '#fff', fontWeight: 600,
                          cursor: 'pointer', opacity: (!draft.text.trim() || !draft.primary_axis.trim()) ? 0.5 : 1 }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
